import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import Button from '../components/ui-components/Button';
import { Card } from '../components/ui-components/Card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Check, Copy, Link as LinkIcon, Mail, ExternalLink } from 'lucide-react';

const ShareLink: React.FC = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  
  const personalLink = user?.personalLink || 'justbookit.com/username';
  
  const copyLink = () => {
    navigator.clipboard.writeText(personalLink);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };
  
  const shareViaEmail = () => {
    const subject = 'Book a meeting with me';
    const body = `Hi,\n\nYou can book a meeting with me using this link: ${personalLink}\n\nLooking forward to our meeting!`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  
  const share = async () => {
    if (!navigator.share) {
      toast.error('Web Share API not supported in your browser');
      return;
    }
    
    try {
      await navigator.share({
        title: 'Book a meeting with me',
        text: 'Use this link to book a meeting with me',
        url: personalLink,
      });
      toast.success('Shared successfully');
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };
  
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12 animate-fade-in">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-6">
            <Check className="h-8 w-8" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Your booking link is ready!</h1>
          <p className="text-xl text-muted-foreground">
            Share your link with anyone who needs to book time with you.
          </p>
        </div>
        
        <Card className="mb-10 p-8 text-center">
          <div className="mb-8">
            <p className="text-sm text-muted-foreground mb-2">Your personal booking link</p>
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="text-xl font-semibold">{personalLink}</span>
              <button 
                onClick={copyLink} 
                className="p-2 rounded-full hover:bg-secondary transition-colors"
                aria-label="Copy link"
              >
                {copied ? 
                  <Check className="h-5 w-5 text-green-500" /> : 
                  <Copy className="h-5 w-5" />
                }
              </button>
            </div>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                variant="outline" 
                leftIcon={<Copy className="h-5 w-5" />}
                onClick={copyLink}
              >
                Copy Link
              </Button>
              
              <Button 
                variant="outline" 
                leftIcon={<Mail className="h-5 w-5" />}
                onClick={shareViaEmail}
              >
                Email
              </Button>
              
              {navigator.share && (
                <Button 
                  variant="outline" 
                  leftIcon={<LinkIcon className="h-5 w-5" />}
                  onClick={share}
                >
                  Share
                </Button>
              )}
            </div>
          </div>
          
          <div className="py-6 border-t border-border">
            <p className="text-sm text-muted-foreground mb-4">Preview your booking page</p>
            <Link to={`/${user?.personalLink?.split('/').pop() || 'username'}`} target="_blank">
              <Button rightIcon={<ExternalLink className="h-5 w-5" />}>
                View Booking Page
              </Button>
            </Link>
          </div>
        </Card>
        
        <div className="text-center space-y-6">
          <h2 className="text-2xl font-semibold">What's next?</h2>
          
          <div className="max-w-lg mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl bg-secondary/50 text-center">
              <h3 className="font-medium mb-2">Connect your calendar</h3>
              <p className="text-sm text-muted-foreground mb-4">Sync your availability automatically</p>
              <Button variant="outline" size="sm">Connect</Button>
            </div>
            
            <div className="p-6 rounded-xl bg-secondary/50 text-center">
              <h3 className="font-medium mb-2">Create another link</h3>
              <p className="text-sm text-muted-foreground mb-4">Make different types of booking links</p>
              <Link to="/create-booking-link">
                <Button variant="outline" size="sm">Create New</Button>
              </Link>
            </div>
          </div>
          
          <div className="pt-8">
            <Link to="/dashboard">
              <Button variant="outline">Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ShareLink;
